import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import CircleInfo from "@dana/Icons/CircleInfo";
import { Cart } from "@dana/Overrides/Services/CartService";
import setIsPlacingOrder from "@dana/Overrides/Store/Pages/OnePageCheckout/Handlers/SetIsPlacingOrder";
import setShippingMethod from "@dana/Overrides/Store/Pages/OnePageCheckout/Handlers/SetShippingMethod";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import setCarrierAccountNumber from "@dana/Store/Pages/OnePageCheckoutExt/Handlers/SetCarrierAccountNumber";
import setOrderNotes from "@dana/Store/Pages/OnePageCheckoutExt/Handlers/SetOrderNotes";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedPickUpDate";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { RadioPresentationProps } from "@insite/mobius/Radio";
import { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import useIsNA from "@dana/Common/Hooks/useIsNA";

const enum fields {
    emeaorderinstructiontooltip = "emeaorderinstructiontooltip",      
}
interface OwnProps extends WidgetProps {
    fields: { 
        [fields.emeaorderinstructiontooltip]: string;
       };
}

const mapStateToProps = (state: ApplicationStateDana) => {
    const cartMode = getCookie('cartMode');
    const {
        accountSettings: { enableWarehousePickup, allowEmptyShipping },
        cartSettings: { canRequestDeliveryDate, enableRequestPickUpDate, maximumDeliveryPeriod },
    } = getSettingsCollection(state);
    const { fulfillmentMethod, pickUpWarehouse } = state.context.session;
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartMode === 'StockOrderCart'
        ? cartState.value
        : cartMode === "EmergencyOrderCart"
            ? state.emergencyCartState.cart as Cart ?? { properties: {} } as Cart
            : state.annualReturnsCartState.cart as Cart ?? { properties: {} } as Cart;
    const { isLoading: isCartLoading } = cartState;
    return {
        cartMode,
        cart,
        isCartLoading: cartMode === 'StockOrderCart' ? isCartLoading : false,
        showShippingMethod: fulfillmentMethod === FulfillmentMethod.Ship,
        showRequestPickUpDate:
            enableWarehousePickup && enableRequestPickUpDate && fulfillmentMethod === FulfillmentMethod.PickUp,
        showRequestDeliveryDate: canRequestDeliveryDate,
        maximumDeliveryPeriod,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        pickUpWarehouse,
        allowEmptyShipping,
        notes: cart?.notes,
        isEmea: useIsEmea(state),
        isNARegion: useIsNA(state),
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setOrderNotes,
    setIsPlacingOrder,
    setCarrierAccountNumber,
};

type Props = OwnProps  & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OnePageCheckoutDeliveryMethodStyles {
    container?: GridContainerProps;
    headingContainer?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    fulfillmentMethodGridItem?: GridItemProps;
    fulfillmentMethodText?: TypographyPresentationProps;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    servicesGridContainer?: GridContainerProps;
    servicesGridItem?: GridItemProps;
    servicesRadioGroup?: RadioGroupPresentationProps;
    serviceRadio?: RadioPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    orderInstuctionTooltip?: TooltipPresentationProps;
}

export const onePageCheckoutDeliveryMethodStyles: OnePageCheckoutDeliveryMethodStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 120px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        transform: "capitalize",
        css: css`
            margin-bottom: 0;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    fulfillmentMethodGridItem: { width: 12 },
    fulfillmentMethodText: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    servicesGridContainer: {
        css: css`
            padding: 16px 16px 0;
        `,
    },
    servicesGridItem: { width: 4 },
    deliveryDateGridItem: { width: 12 },
    pickUpDateGridItem: { width: 12 },
};

const styles = onePageCheckoutDeliveryMethodStyles;

const sanitizeOrderNotes = (value: string | null | undefined) =>
    value?.replace(/[^A-Za-z0-9 .,!?\'"():;&\/-]/g, "") ?? "";

const OnePageCheckoutDeliveryMethod = ({
    cartMode,
    cart,
    isCartLoading,
    cartUrl,
    pickUpWarehouse,
    showShippingMethod,
    showRequestPickUpDate,
    showRequestDeliveryDate,
    maximumDeliveryPeriod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setShippingMethod,
    allowEmptyShipping,
    notes,
    setOrderNotes,
    setIsPlacingOrder,
    setCarrierAccountNumber,
    isEmea,
    fields,
    isNARegion,
}: Props) => {
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [isUpdatingCart, setIsUpdatingCart] = useState(false);
    const [orderNotes, setOrderNotesText] = useState(() => sanitizeOrderNotes(notes));
    const [carrierAccountNumber, setCarrierAccountNumberText] = useState("");
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [errorAccount, setErrorAccount] = useState(false);
    const [isErrorShipVia, setisErrorShipVia] = useState(false);
    const [isErrorCarrier, setisErrorCarrier] = useState(false);
    const [errorCarrierMsg, seterrorCarrierMsg] = useState("");
    const [errorShipViaMsg, seterrorShipViaMsg] = useState("");
    const [errorMsg, seterrorMsg] = useState("");

    useEffect(() => {
        const sanitizedNotes = sanitizeOrderNotes(notes);
        setOrderNotesText(sanitizedNotes);
        if (sanitizedNotes !== (notes ?? "")) {
            setOrderNotes({ orderNotes: sanitizedNotes });
        }
    }, [notes, setOrderNotes]);
  

    useEffect(() => {
        if (selectedCarrier?.description?.indexOf("♦") && carrierAccountNumber === "") {
            setErrorAccount(true);
            seterrorMsg(translate("Carrier Account Number is required."));
            setIsPlacingOrder({ isPlacingOrder: false });
        }
    }, [selectedCarrier?.description?.indexOf("♦"), carrierAccountNumber]);

    useEffect(() => {
        if (selectedCarrier === null) {
            setisErrorCarrier(true);
            seterrorCarrierMsg(translate("Select Freight Handling."));
        }
        if (selectedShipVia === null) {
            setisErrorShipVia(true);
            seterrorShipViaMsg(translate("Select Carrier."));
        }
    }, [selectedCarrier, selectedShipVia]);

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="onePageCheckout_deliveryMethodLoading" />
            </StyledWrapper>
        );
    }

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const sanitizedNotes = sanitizeOrderNotes(event.currentTarget.value);
        setOrderNotesText(sanitizedNotes);
        setOrderNotes({ orderNotes: sanitizedNotes });
    };

    const shipViaChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        if (!selectedCarrier) {
            return;
        }
        if (event.currentTarget.value === "") {
            setisErrorShipVia(true);
            seterrorShipViaMsg(translate("Select Carrier."));
        } else {
            setisErrorShipVia(false);
            seterrorShipViaMsg("");
        }
        const selectedShipVia = selectedCarrier.shipVias!.find(s => s.id === event.currentTarget.value)!;
        setIsUpdatingCart(true);
        setSelectedShipVia(selectedShipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: selectedShipVia,
            onComplete: () => { },
        });
    };

    const carrierChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        if (event.currentTarget.value === "") {
            setisErrorCarrier(true);
            setisErrorShipVia(true);
            seterrorCarrierMsg(translate("Select Freight Handling."));
            seterrorShipViaMsg(translate("Select Carrier."));
            setSelectedCarrier(null);
            setSelectedShipVia(null);
        } else {
            setisErrorCarrier(false);
            setisErrorShipVia(false);
            seterrorCarrierMsg("");
            seterrorShipViaMsg("");
        }
        setCarrierAccountNumberText("");
        setCarrierAccountNumber({ carrierAccountNumber: "" });
        const selectedCarrier = cart.carriers!.find(c => c.id === event.currentTarget.value)!;
        const defaultShipVias = selectedCarrier.shipVias!.filter(o => o.isDefault);
        const shipVia = defaultShipVias.length > 0 ? defaultShipVias[0] : undefined;
        setSelectedCarrier(selectedCarrier);
        setSelectedShipVia(null);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia,
            onComplete: () => { },
        });
    };
    const changeCarrierAccountNumberHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = isNARegion ? event.currentTarget.value.toUpperCase() : event.currentTarget.value;
        if (value !== "") {
            setErrorAccount(false);
        }
        setCarrierAccountNumberText(value);
        setCarrierAccountNumber({ carrierAccountNumber: value });
    };

    if (cartMode === "AnnualReturnsOrderCart"){
        return null;
    }

 
    const tooltipContent = (
        <Tooltip
            text={fields.emeaorderinstructiontooltip}
            {...styles.orderInstuctionTooltip}
        />
    );

    return (
        <GridContainer {...styles.container}>
            <GridContainer gap={0} {...styles.headingContainer}>
                <GridItem {...styles.titleGridItem}>
                    <Typography as="h2" {...styles.titleText}>
                        {translate("Specify Shipping")}
                    </Typography>
                </GridItem>
            </GridContainer>
            {showShippingMethod && (
                <GridContainer {...styles.servicesGridContainer}>
                    {cart.carriers!.length > 0 && (
                        <>
                            <GridItem {...styles.servicesGridItem}>
                                <Select
                                    label={translate("Freight Handling")}
                                    required
                                    value={selectedCarrier?.id || ""}
                                    onChange={carrierChangeHandler}
                                    error={isErrorCarrier && errorCarrierMsg}
                                    data-test-selector="onepagecheckout_carrierSelect"
                                    name="carrier"
                                >
                                    <option value="">
                                        {translate("Select")}
                                    </option>
                                    {[...cart.carriers!]
                                        .sort((a, b) => a.description?.replace("♦", "").localeCompare(b.description?.replace("♦", "")))
                                        .map(c => {
                                            const id = c.id!.toString();
                                            return (
                                                <option key={id} value={id}>
                                                    {c.description?.replace("♦", "")}
                                                </option>
                                            );
                                        })
                                    }

                                </Select>
                            </GridItem>
                            {(selectedCarrier && selectedCarrier.id !== null && selectedCarrier?.shipVias?.length !== undefined &&
                                selectedCarrier.description !== "Pick-up / Will Call") && (
                                    <>
                                        <GridItem {...styles.servicesGridItem} className="carrier-service">
                                            <Select
                                                label={translate("Carrier")}
                                                required
                                                error={isErrorShipVia && errorShipViaMsg}
                                                value={selectedShipVia?.id || ""}
                                                onChange={shipViaChangeHandler}
                                                data-test-selector="onepagecheckout_shippingServiceSelect"
                                            >
                                                <option value="">
                                                    {translate("Select")}
                                                </option>
                                                {selectedCarrier &&
                                                    selectedCarrier.shipVias!.map(s => {
                                                        const id = s.id.toString();
                                                        return (
                                                            <option key={id} value={id}>
                                                                {s.description}
                                                            </option>
                                                        );
                                                    })}
                                            </Select>
                                            {selectedCarrier.description === "Pre-pay and Add to Invoice" && (
                                                <Typography className="info-message-box">
                                                    <IconMemo size={13} src={CircleInfo} /> {translate("Preferred Carrier is FedEx. Other carriers may be subject to higher cost")}
                                                </Typography>
                                            )}
                                        </GridItem>
                                        {selectedCarrier?.description?.indexOf("♦") > 0 && (
                                            <GridItem {...styles.servicesGridItem}>
                                                <TextField
                                                    data-test-selector="textcarrierAccountNumber"
                                                    value={carrierAccountNumber}
                                                    onChange={changeCarrierAccountNumberHandler}
                                                    required
                                                    error={errorAccount && errorMsg}
                                                    label={translate("Carrier Account No.")}
                                                ></TextField>
                                            </GridItem>
                                        )}
                                    </>
                                )}
                        </>
                    )}
                </GridContainer>
            )}
            <GridItem {...styles.fulfillmentMethodGridItem}>
                <TextArea
                    label= {
                        <>
                          {isEmea
                            ? translate("Order Instructions (optional)")
                            : translate("Shipping Instruction (optional)")
                          }{" "}
                          {tooltipContent}
                        </>
                    }
                    value={orderNotes}
                    onChange={changeHandler}                    
                /> 
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutDeliveryMethod),
    definition: {
        displayName: "Delivery Method",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
        fieldDefinitions: [
            {
                name:fields.emeaorderinstructiontooltip,          
                displayName: "Order Instructions Tooltip for EMEA Region",
                editorTemplate: "TextField",
                defaultValue: "Please add order notes.",
                fieldType: "General",
            },           
        ],
    },
};

export default widgetModule;
